import { state } from "./state";
import { RADIUS } from "./levels";
import { covers, inBounds, key } from "./geometry";
import { cellEls } from "./board";
import { makeNoriaEl } from "./norias";
import { gridEl, previewEl, stageEl } from "../dom";
import { recompute } from "./flow";

/* ---------- Placement queries ---------- */
function noriaAt(r: number, c: number) {
  return state.norias.find((n) => n.r === r && n.c === c) || null;
}
function isPlaceable(r: number, c: number, ignoreId?: number): boolean {
  if (!inBounds(r, c)) return false;
  if (state.treeSet.has(key(r, c)) || state.rockSet.has(key(r, c))) return false;
  const n = noriaAt(r, c);
  if (n && n.id !== ignoreId) return false;
  return true;
}

/* ---------- Preview ---------- */
let previewCells: HTMLElement[] = [];
export function clearPreview(): void {
  previewEl.innerHTML = "";
  for (const el of previewCells) el.classList.remove("preview");
  previewCells = [];
}

function showPreviewAt(r: number, c: number): void {
  clearPreview();
  const cell = state.cell, R = RADIUS;
  const circ = document.createElement("div");
  circ.className = "irrig ghost";
  const dia = 2 * R * cell;
  circ.style.width = dia + "px";
  circ.style.height = dia + "px";
  circ.style.left = (c * cell + cell / 2 - R * cell) + "px";
  circ.style.top = (r * cell + cell / 2 - R * cell) + "px";
  previewEl.appendChild(circ);

  const size = cell * 1.28;
  const g = makeNoriaEl("ghost");
  g.style.width = size + "px";
  g.style.height = size + "px";
  g.style.left = (c * cell + cell / 2 - size / 2) + "px";
  g.style.top = (r * cell + cell / 2 - size / 2) + "px";
  previewEl.appendChild(g);

  const L = state.level();
  for (let rr = 0; rr < L.rows; rr++)
    for (let cc = 0; cc < L.cols; cc++) {
      if (state.rockSet.has(key(rr, cc))) continue;
      if (covers(r, c, rr, cc)) {
        cellEls[rr][cc].classList.add("preview");
        previewCells.push(cellEls[rr][cc]);
      }
    }
}

/* ---------- Floaty feedback ---------- */
function floaty(text: string, r: number, c: number, type: "gain" | "loss"): void {
  const f = document.createElement("div");
  f.className = "floaty " + type;
  f.textContent = text;
  f.style.left = (c * state.cell + state.cell * 0.18) + "px";
  f.style.top = r * state.cell + "px";
  previewEl.appendChild(f);
  setTimeout(() => f.remove(), 1000);
}

/* ---------- Pointer ---------- */
function cellFromEvent(e: PointerEvent): { r: number; c: number } {
  const rect = gridEl.getBoundingClientRect();
  const c = Math.floor((e.clientX - rect.left) / state.cell);
  const r = Math.floor((e.clientY - rect.top) / state.cell);
  return { r, c };
}

function onPointerDown(e: PointerEvent): void {
  if (state.phase !== "play") return;
  const { r, c } = cellFromEvent(e);
  if (!inBounds(r, c)) return;
  e.preventDefault();

  const n = noriaAt(r, c);
  if (n) {
    state.drag = {
      id: n.id, originR: r, originC: c, startX: e.clientX, startY: e.clientY,
      moved: false, pointerId: e.pointerId, target: { r, c }, valid: true,
    };
    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);
    window.addEventListener("pointercancel", onUp);
    return;
  }

  if (isPlaceable(r, c)) {
    if (state.norias.length < state.level().norias) {
      state.norias.push({ id: state.nextId++, r, c });
      floaty("+water", r, c, "gain");
      clearPreview();
      recompute();
    } else {
      pulseNoTokens();
    }
  }
}

function onMove(e: PointerEvent): void {
  if (!state.drag) return;
  e.preventDefault();
  if (!state.drag.moved) {
    if (Math.hypot(e.clientX - state.drag.startX, e.clientY - state.drag.startY) < 6) return;
    state.drag.moved = true;
    const el = document.querySelector('.noria[data-id="' + state.drag.id + '"]');
    if (el) el.classList.add("lifted");
  }
  const { r, c } = cellFromEvent(e);
  const valid = isPlaceable(r, c, state.drag.id) || (r === state.drag.originR && c === state.drag.originC);
  state.drag.target = { r, c };
  state.drag.valid = valid && inBounds(r, c);
  if (inBounds(r, c)) showPreviewAt(r, c);
  else clearPreview();
}

function onUp(): void {
  window.removeEventListener("pointermove", onMove);
  window.removeEventListener("pointerup", onUp);
  window.removeEventListener("pointercancel", onUp);
  if (!state.drag) return;
  const d = state.drag;
  const n = state.norias.find((x) => x.id === d.id);
  clearPreview();
  if (!d.moved) {
    // tap = remove
    if (n) {
      floaty("−water", n.r, n.c, "loss");
      state.norias = state.norias.filter((x) => x.id !== d.id);
    }
  } else {
    const { r, c } = d.target;
    if (n && inBounds(r, c) && isPlaceable(r, c, d.id)) { n.r = r; n.c = c; }
    // else: invalid drop -> snap back (no change)
  }
  state.drag = null;
  recompute();
}

function onHover(e: PointerEvent): void {
  if (state.drag) return;
  if (e.buttons !== 0) return; // only true hover
  if (state.phase !== "play") return;
  const { r, c } = cellFromEvent(e);
  if (inBounds(r, c) && isPlaceable(r, c) && state.norias.length < state.level().norias) {
    showPreviewAt(r, c);
  } else {
    clearPreview();
  }
}

function pulseNoTokens(): void {
  const el = document.getElementById("noriaVal")?.parentElement;
  el?.animate(
    [{ transform: "translateX(0)" }, { transform: "translateX(-5px)" }, { transform: "translateX(5px)" }, { transform: "translateX(0)" }],
    { duration: 260 },
  );
  const msg = document.getElementById("msg");
  if (msg) { msg.className = "msg"; msg.textContent = "No norias left — pick one up to move it elsewhere."; }
}

/** Wire up all board pointer interaction. Call once at init. */
export function initInteraction(): void {
  stageEl.addEventListener("pointerdown", onPointerDown);
  stageEl.addEventListener("pointermove", onHover);
  stageEl.addEventListener("pointerleave", () => { if (!state.drag) clearPreview(); });
}
